import { convert, inferUnit } from 'util/unitConversion';
import { indexOfMaxItemArray } from 'util/indexOfMaxItemArray';
import type { EconomicParameters } from './useSeedDensityRecommendation';
import type { SeedDensityRecommendationQueryResponse } from '../../types';

interface GetEconomicRecommendationParams {
  curve: SeedDensityRecommendationQueryResponse['seedingRateRecommendation']['curve'];
  economicParameters: EconomicParameters;
}

/**
 * Calculates the economically optimal seeding rate recommendation based on profit maximization.
 * 
 * Generates estimated profits for each planting rate in the curve by calculating the difference
 * between estimated income (based on yield and grain price) and seed costs. Returns the
 * planting rate that yields the highest profit.
 */
export const getEconomicRecommendation = ({ curve, economicParameters }: GetEconomicRecommendationParams) => {
  const estimatedProfits = curve.plantingRate.map((rate, index) => {
    const serviceMassYieldunit = curve.yieldUnit.split('/')[0];
    const convertedGrainPrice = convert({
      from: { u: `usd/${inferUnit('mass.yield').unitKey}`, q: economicParameters.grainPrice },
      to: { u: `usd/${serviceMassYieldunit}` },
      cropNominalWeight: economicParameters.cropNominalWeight
    });

    const seedCost = (rate / economicParameters.seedsPerBag) * economicParameters.seedCostPerBag;
    const estimatedIncome = convertedGrainPrice.q * curve.yield[index];
    const estimatedProfit = estimatedIncome - seedCost;
    return estimatedProfit;
  });

  const highestProfitIndex = indexOfMaxItemArray(estimatedProfits);
  return {
    v: curve.plantingRate[highestProfitIndex],
    u: curve.plantingRateUnit,
  };
};
